import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Team {
  id: number;
  name: string;
  industry?: string;
  description?: string;
  website?: string;
  linkedinUrl?: string;
  companySize?: string;
  minBudget?: number;
  maxBudget?: number;
  services?: string[];
  targetMarkets?: string[];
  createdAt: string;
  updatedAt: string;
  role: string;
  joinedAt: string;
  memberCount: number;
}

export interface TeamState {
  currentTeam: Team | null;
  teams: Team[];
  isLoading: boolean;
}

export interface TeamActions {
  setCurrentTeam: (team: Team | null) => void;
  setTeams: (teams: Team[]) => void;
  setLoading: (loading: boolean) => void;
  getCurrentTeamId: () => number | null;
  switchTeam: (teamId: number) => void;
  clearTeams: () => void;
}

const initialState: TeamState = {
  currentTeam: null,
  teams: [],
  isLoading: true,
};

export const useTeamStore = create<TeamState & TeamActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      setCurrentTeam: (team: Team | null) => {
        set({ currentTeam: team });
      },

      setTeams: (teams: Team[]) => {
        const { currentTeam } = get();
        
        // If no current team is set, set the first team as current
        if (!currentTeam && teams.length > 0) {
          set({ 
            teams, 
            currentTeam: teams[0] || null,
            isLoading: false 
          });
        } else {
          // Update current team if it exists in the new teams list
          const updatedCurrentTeam = currentTeam ? 
            teams.find(t => t.id === currentTeam.id) || teams[0] || null : 
            null;
          
          set({ 
            teams, 
            currentTeam: updatedCurrentTeam,
            isLoading: false 
          });
        }
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      getCurrentTeamId: (): number | null => {
        return get().currentTeam?.id || null;
      },

      switchTeam: (teamId: number) => {
        const { teams } = get();
        const team = teams.find(t => t.id === teamId);
        if (team) {
          set({ currentTeam: team });
        }
      },

      clearTeams: () => {
        set({
          ...initialState,
          isLoading: false,
        });
      },
    }),
    {
      name: 'leadgpt-team-storage',
      partialize: (state) => ({
        currentTeam: state.currentTeam,
        teams: state.teams,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setLoading(false);
        }
      },
    },
  ),
);

// Selectors for easier usage
export const useTeam = () => {
  const {
    currentTeam,
    teams,
    isLoading,
    setCurrentTeam,
    setTeams,
    setLoading,
    getCurrentTeamId,
    switchTeam,
    clearTeams,
  } = useTeamStore();

  return {
    currentTeam,
    teams,
    isLoading,
    setCurrentTeam,
    setTeams,
    setLoading,
    getCurrentTeamId,
    switchTeam,
    clearTeams,
  };
};